/**
 * Refetch-on-focus hook
 *
 * Invokes the provided refetch callback when the window regains visibility or
 * the browser comes back online — moments where realtime channels may have
 * silently dropped changes (laptop lid closed, tab suspended, network blip).
 * Throttled so rapid tab switches don't hammer the backend.
 *
 * The `online` path additionally defers to the offline queue: the same event
 * starts the queue replay, and a refetch that wins that race pulls server state
 * which does not contain the queued writes yet, so the store replaces its rows
 * wholesale and the user watches their offline edits vanish. It waits for the
 * queue to be DRAINED rather than merely idle — a run that stops on a backoff
 * goes idle with the writes still queued — bounded by
 * {@link REPLAY_WAIT_TIMEOUT_MS} so a permanently stuck queue cannot cost the
 * refetch entirely.
 *
 * @module shared/hooks/useRefetchOnFocus
 */

import { useEffect, useRef } from 'react';
import {
  getCachedPendingCount,
  isReplaying,
  subscribeQueueChange,
} from '@/shared/lib/offlineMutationQueue';

/** Minimum interval between two focus/online-triggered refetches. */
export const REFETCH_THROTTLE_MS = 30_000;

/**
 * Upper bound on how long an `online` refetch waits for the queue. A replay
 * that stalls (a poisoned entry retrying, a dead connection) must not cost the
 * refetch entirely — a late refetch beats none at all. Matches the queue's own
 * `REPLAY_ARM_TIMEOUT_MS`, after which an armed-but-never-run replay releases
 * itself anyway.
 */
export const REPLAY_WAIT_TIMEOUT_MS = 15_000;

/** True when nothing is in flight AND nothing is left queued. */
function isQueueSettled(): boolean {
  return !isReplaying() && getCachedPendingCount() === 0;
}

/**
 * Resolve once the queue is DRAINED, or after `timeoutMs` — whichever first.
 *
 * Waiting for the replay phase alone was not enough. A run that stops on a
 * retryable failure — a 503 from the edge, a still-flapping link — writes the
 * entry back with a backoff and then returns the phase to idle with the write
 * still queued. Every idle waiter resolved there, the refetch ran, and the
 * store replaced its rows with server state that does not contain the queued
 * change: the offline row the user is looking at disappears, and for a queued
 * DELETE the row they deleted comes back. So the wait is on the count reaching
 * zero, which the backoff retry run does a few seconds later.
 */
async function raceQueueDrained(timeoutMs: number): Promise<void> {
  if (isQueueSettled()) {
    return;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  let unsubscribe: (() => void) | undefined;
  try {
    await new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
      unsubscribe = subscribeQueueChange(() => {
        if (isQueueSettled()) {
          resolve();
        }
      });
      // The queue can have drained between the guard above and the subscribe.
      if (isQueueSettled()) {
        resolve();
      }
    });
  } finally {
    clearTimeout(timer);
    unsubscribe?.();
  }
}

export function useRefetchOnFocus(refetch: () => void): void {
  // Latest-callback pattern: don't re-bind listeners on every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    // Start throttled: the page just mounted, stores load fresh anyway.
    let lastRun = Date.now();
    let disposed = false;
    /** True while an `online` refetch is waiting for the replay to finish. */
    let awaitingReplay = false;

    const runRefetch = (): void => {
      lastRun = Date.now();
      refetchRef.current();
    };

    const maybeRefetch = (): void => {
      const now = Date.now();
      if (now - lastRun < REFETCH_THROTTLE_MS) {
        return;
      }
      runRefetch();
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        maybeRefetch();
      }
    };

    const handleOnline = (): void => {
      // `isReplaying()` flips synchronously on this same `online` event when
      // the queue is non-empty, so reading it here cannot miss a replay that
      // is about to start. The count is checked too, because a write enqueued
      // while the browser still reported "online" is replayed by a timer
      // rather than by an arming, and it owns rows the refetch would wipe.
      if (isQueueSettled()) {
        maybeRefetch();
        return;
      }
      if (awaitingReplay) {
        return;
      }
      awaitingReplay = true;
      void (async () => {
        try {
          await raceQueueDrained(REPLAY_WAIT_TIMEOUT_MS);
        } finally {
          awaitingReplay = false;
        }
        if (disposed) {
          return;
        }
        // Deliberately not throttled: this refetch is the reconciliation half
        // of the replay. A visibility refetch moments earlier is exactly the
        // case that wiped the queued rows out of the store, so throttling it
        // away would leave them missing until the next focus event.
        runRefetch();
      })();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
}
